import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import FormsElementContainer from '../FormsElementContainer/FormsElementContainer';

// Utilities
import { createDescribedById } from '../FormLabel/utilities';
import { getCharCount } from '../../lib/inputHelper';

// Shape
import EnumerableShape from '../../../../../lib/shapes/EnumerableShape';

// Enumerables
import { TooltipPlacement } from '../../../../atoms/Tooltip';
import { FormLabelModifier } from '../FormLabel/FormLabel';

// Components
import FormLabel from '../FormLabel/FormLabel';

import { getComputedStyle } from '../../../../../lib/window/Window';
import { appendClassName } from '../../../../../lib/bemUtils/classname';
import FormFieldWrapper from '../FormFieldWrapper/FormFieldWrapper';
import { Namespace } from '../../../../../translation/namespace';

/**
 * Renders a Textarea with an Label
 */
class FormTextarea extends Component {
  static parseIntFromStyleAttributeValues(value, measurementUnitCode, base) {
    if (value.endsWith('%')) {
      return Number.MAX_SAFE_INTEGER;
    }

    return parseInt(value.replace(measurementUnitCode, ''), base);
  }

  static autoExpand(target) {
    const element = target;
    const computedStyle = getComputedStyle(element);
    const offset = element.offsetHeight - element.clientHeight;

    setTimeout(() => {
      element.style.height = '0';
      element.style.height = `${element.scrollHeight - offset}px`;

      if (
        FormTextarea.parseIntFromStyleAttributeValues(element.style.height, 'px', 10) >=
        FormTextarea.parseIntFromStyleAttributeValues(computedStyle.maxHeight, 'px', 10)
      ) {
        element.style.overflow = 'auto';
      } else {
        element.style.overflow = 'hidden';
      }
    }, 0);
  }

  constructor(props) {
    super(props);

    this.textArea = React.createRef();

    this._idTextarea = `${this.props.name}-${this.props.id}`;
    this.state = {
      value: this.props.value,
      charCount: 0,
    };

    this.onChange = this.onChange.bind(this);
  }

  componentDidMount() {
    if (this.props.autoExpand === true) {
      FormTextarea.autoExpand(this.textArea.current);
    }

    // Validate if the text value provided by BE is too long (mostly due to old data)
    if (this.props.value && this.props.maxLength && this.props.value.length > this.props.maxLength) {
      this.setCustomValidity(this.props.t(`${Namespace.Form}:tooManyCharacters`));
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.value && this.props.value !== prevProps.value) {
      const isValidLength = this.props.value.length <= this.props.maxLength;
      if (isValidLength || !this.props.maxLength) {
        // ok if setState is in an if clause
        this.setState({ value: this.props.value });
      }
    }
    if (!this.props.value && prevProps.value) {
      // OK to setState here within an if clause
      this.setState({ value: null });
    }
  }

  get idTextarea() {
    return this._idTextarea;
  }

  setCustomValidity(message = '') {
    if (this.textArea.current && this.textArea.current.setCustomValidity) {
      if (message) {
        this.textArea.current.setCustomValidity(message);
        return true;
      }
      this.textArea.current.setCustomValidity('');
      return false;
    }
    return false;
  }

  // This function is a wrapper for setCustomValidity (improve semantic)
  unsetCustomValidity() {
    this.setCustomValidity();
  }

  onChange(event) {
    this.unsetCustomValidity();

    const { value } = event.target;

    this.setState({ value });
    this.props.onChange(event);

    // fields can not! only contain white spaces.
    // check for >= 1 for normal validation of fields
    if (value.length >= 1 && value?.trim?.() === '') {
      this.setCustomValidity(this.props.t(`${Namespace.Form}:fieldCantBeEmpty`));
    }

    if (this.props.autoExpand === true) {
      FormTextarea.autoExpand(event.target);
    }

    // feed the char counter with data
    this.setState({ charCount: getCharCount(event) });
  }

  render() {
    const classesTextarea = appendClassName('form-textarea', this.props.modifierTextarea || '');

    return (
      <FormsElementContainer>
        <FormLabel
          htmlFor={this.idTextarea}
          required={this.props.required}
          helpText={this.props.labelHelpText}
          tooltipPlacement={this.props.tooltipPlacement}
          modifier={this.props.modifierLabel}
        >
          {this.props.labelTitle}
        </FormLabel>
        <FormFieldWrapper
          maxLength={this.props.maxLength && !this.props.readOnly ? this.props.maxLength : null}
          currentCount={this.state.charCount}
        >
          {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
          <textarea
            className={classesTextarea}
            ref={this.textArea}
            cols="40"
            rows="20"
            name={this.props.name}
            id={this.idTextarea}
            disabled={this.props.disabled}
            readOnly={this.props.readOnly}
            placeholder={this.props.placeholder}
            value={this.state.value || ''}
            // Validation
            required={this.props.required}
            maxLength={this.props.maxLength}
            // Accessability
            aria-describedby={this.props.labelHelpText && createDescribedById(this.idTextarea)}
            // Events
            onChange={this.onChange}
            onFocus={event => {
              this.setState({ charCount: getCharCount(event) });
            }}
          />
        </FormFieldWrapper>
      </FormsElementContainer>
    );
  }
}

export default withTranslation()(FormTextarea);

FormTextarea.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  labelTitle: PropTypes.string.isRequired,
  labelHelpText: PropTypes.string,
  tooltipPlacement: EnumerableShape(TooltipPlacement),
  autoExpand: PropTypes.bool,
  t: PropTypes.func.isRequired,

  // Validations
  required: PropTypes.bool,
  maxLength: PropTypes.number,
  readOnly: PropTypes.bool,

  // Modifier
  modifierLabel: EnumerableShape(FormLabelModifier),
  modifierTextarea: PropTypes.string,

  // Events
  onChange: PropTypes.func,
};

FormTextarea.defaultProps = {
  maxLength: null,
  required: false,
  disabled: false,
  readOnly: false,
  placeholder: null,
  value: '',
  labelHelpText: undefined,
  tooltipPlacement: undefined,
  autoExpand: false,
  modifierLabel: undefined,
  modifierTextarea: undefined,
  // Events
  onChange: () => {},
};
